import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createProductUrl } from '../utils/paths';
import { useAccount } from '../contexts/AccountContext';
import { useToast } from '../contexts/ToastContext';
import { useApiCall } from '../hooks/useApiCall';
import Modal from '../components/Modal';
import { 
  RiUserLine,
  RiShoppingBagLine,
  RiHeartLine,
  RiSettingsLine,
  RiLogoutBoxLine,
  RiEditLine,
  RiSaveLine,
  RiCloseLine,
  RiEyeLine,
  RiPhoneLine,
  RiMailLine,
  RiTruckLine,
  RiCheckboxCircleLine,
  RiCloseCircleLine,
  RiTimeLine,
  RiCalendarLine
} from 'react-icons/ri';
import '../styles/Account.css';

const Account = () => {
  const navigate = useNavigate();
  const { tab } = useParams();
  const { account, updateAccount, clearAccount } = useAccount();
  const { showSuccess, showError } = useToast();
  const apiCall = useApiCall();
  const [activeTab, setActiveTab] = useState(tab || 'profile');
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_cc: '597',
    phone: ''
  });

  // Orders data
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersTotalPages, setOrdersTotalPages] = useState(1);

  // Sync activeTab with URL parameter
  useEffect(() => {
    setActiveTab(tab || 'profile');
  }, [tab]);

  // Fetch account info and initialize profile form
  useEffect(() => {
    const fetchAccountInfo = async () => {
      if (account && account.token) {
        try {
          const response = await apiCall('account/info', 'GET', null, account, null);
          
          if (response.success && response.data?.account) {
            const accountData = response.data.account;
            
            // Update the profile form with fetched data
            setProfileForm({
              first_name: accountData.first_name || '',
              last_name: accountData.last_name || '',
              email: accountData.email || '',
              phone_cc: accountData.phone_cc || '597',
              phone: accountData.phone || ''
            });
            
            // Update the account context with complete data
            updateAccount(accountData);
          } else {
            console.error('Failed to fetch account info:', response.message);
          }
        } catch (error) {
          console.error('Error fetching account info:', error);
        }
      }
    };

    fetchAccountInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.token]);

  // Fetch orders when orders tab is active
  useEffect(() => {
    const fetchOrders = async () => {
      if (activeTab === 'orders' && account && account.token) {
        setOrdersLoading(true);
        try {
          const response = await apiCall('account/orders/get', 'POST', {
            page: ordersPage,
            limit: 10
          }, account, null);
          
          if (response.success && response.data) {
            setOrders(response.data.orders || []);
            setOrdersTotalPages(response.data.total_pages || 1);
          } else {
            console.error('Failed to fetch orders:', response.message);
            showError(response.message || 'Failed to load orders');
          }
        } catch (error) {
          console.error('Error fetching orders:', error);
          showError('Failed to load orders');
        } finally {
          setOrdersLoading(false);
        }
      }
    };

    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, ordersPage]);

  // Redirect if not signed in
  useEffect(() => {
    if (!account) {
      navigate('/sign-in');
    }
  }, [account, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveProfile = async () => {
    setIsLoading(true);
    try {
      const response = await apiCall('account/update', 'POST', {
        first_name: profileForm.first_name.trim(),
        last_name: profileForm.last_name.trim(),
        email: profileForm.email.trim(),
        phone_cc: profileForm.phone_cc,
        phone: profileForm.phone.trim()
      }, account, null);

      if (response.success) {
        // Update account context with the response data
        updateAccount(response.data?.account);
        setIsEditing(false);
        showSuccess('Profile updated successfully');
      } else {
        console.error('Profile update failed:', response.message);
        showError(response.message || 'Failed to update profile. Please try again.');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      showError('Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = () => {
    clearAccount();
    navigate('/');
    setActiveModal(null);
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordSubmit = async () => {
    if (!passwordForm.currentPassword) {
      showError('Current password is required');
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showError('New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      showError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiCall('account/password/change', 'POST', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
        confirm_password: passwordForm.confirmPassword
      }, account, null);

      if (response.success) {
        // Reset form and close modal
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setActiveModal(null);
        showSuccess('Password updated successfully');
      } else {
        showError(response.message || 'Failed to update password. Please try again.');
      }
    } catch (error) {
      console.error('Error updating password:', error);
      showError('Failed to update password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'delivered':
        return <RiCheckboxCircleLine className="status-icon delivered" />;
      case 'ready_to_deliver':
      case 'shipped':
        return <RiTruckLine className="status-icon shipped" />;
      case 'pending':
      case 'processing':
        return <RiTimeLine className="status-icon processing" />;
      case 'cancelled':
        return <RiCloseCircleLine className="status-icon cancelled" />;
      default:
        return <RiTimeLine className="status-icon" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'delivered':
        return 'Delivered';
      case 'ready_to_deliver':
        return 'Ready to Deliver';
      case 'shipped':
        return 'Shipped';
      case 'pending':
        return 'Pending';
      case 'processing':
        return 'Processing';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status || 'Unknown';
    }
  };

  if (!account) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="account-container">
      <div className="account-content">
        <div className="account-sidebar">
          <div className="account-user-info">
            <div className="account-avatar">
              <RiUserLine />
            </div>
            <h2>Welcome back, {account.name || account.email}</h2>
            <p>{account.email}</p>
          </div>

          <nav className="account-nav">
            <button 
              className={`account-nav-item ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => navigate('/account')}
            >
              <RiUserLine />
              <span>Profile</span>
            </button>
            <button 
              className={`account-nav-item ${activeTab === 'orders' ? 'active' : ''}`}
              onClick={() => navigate('/account/orders')}
            >
              <RiShoppingBagLine />
              <span>Orders</span>
            </button>
            <button 
              className={`account-nav-item ${activeTab === 'wishlist' ? 'active' : ''}`}
              onClick={() => navigate('/wishlist')}
            >
              <RiHeartLine />
              <span>Wishlist</span>
            </button>
            <button 
              className={`account-nav-item ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => navigate('/account/settings')}
            >
              <RiSettingsLine />
              <span>Settings</span>
            </button>
            <button 
              className="account-nav-item signout-btn"
              onClick={() => setActiveModal('signout')}
            >
              <RiLogoutBoxLine />
              <span>Sign out</span>
            </button>
          </nav>
        </div>

        <div className="account-main">
          {activeTab === 'profile' && (
            <div className="account-section">
              <div className="section-header">
                <h3>Profile information</h3>
                {!isEditing ? (
                  <button 
                    className="edit-btn"
                    onClick={() => setIsEditing(true)}
                  >
                    <RiEditLine />
                    Edit profile
                  </button>
                ) : (
                  <div className="edit-actions">
                    <button 
                      className="save-btn"
                      onClick={handleSaveProfile}
                      disabled={isLoading}
                    >
                      <RiSaveLine />
                      {isLoading ? 'Saving...' : 'Save'}
                    </button>
                    <button 
                      className="cancel-btn"
                      onClick={() => setIsEditing(false)}
                    >
                      <RiCloseLine />
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              <div className="profile-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label>First name</label>
                    <div className="input-wrapper">
                      <RiUserLine className="input-icon" />
                      <input
                        type="text"
                        name="first_name"
                        value={profileForm.first_name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Enter your first name"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Last name</label>
                    <div className="input-wrapper">
                      <RiUserLine className="input-icon" />
                      <input
                        type="text"
                        name="last_name"
                        value={profileForm.last_name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Enter your last name"
                      />
                    </div>
                  </div>

                  <div className="form-group full-width">
                    <label>Email address</label>
                    <div className="input-wrapper">
                      <RiMailLine className="input-icon" />
                      <input
                        type="email"
                        name="email"
                        value={profileForm.email}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Phone Number</label>
                    <div className="phone-input-wrapper">
                      <select
                        name="phone_cc"
                        value={profileForm.phone_cc}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className="phone-code-select"
                      >
                        <option value="597">+597</option>
                        <option value="31">+31</option>
                      </select>
                      <div className="input-wrapper phone-input">
                        <RiPhoneLine className="input-icon" />
                        <input
                          type="tel"
                          name="phone"
                          value={profileForm.phone}
                          onChange={handleInputChange}
                          disabled={!isEditing}
                          placeholder="Enter your phone number"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'orders' && (
            <div className="account-section">
              <div className="section-header">
                <h3>Order history</h3>
                <p>Track and manage your orders</p>
              </div>

              {ordersLoading ? (
                <div className="loading-state">Loading orders...</div>
              ) : orders.length === 0 ? (
                <div className="empty-state">
                  <RiShoppingBagLine />
                  <h4>No orders yet</h4>
                  <p>Start shopping to see your orders here</p>
                  <button className="btn btn-primary" onClick={() => navigate('/products')}>
                    Browse products
                  </button>
                </div>
              ) : (
                <>
                  <div className="orders-list">
                    {orders.map((order) => (
                      <div key={order.id} className="order-card">
                        <div className="order-header">
                          <div className="order-info">
                            <h4>Order #{order.id}</h4>
                            <p className="order-date">
                              <RiCalendarLine />
                              {new Date(order.timestamp_placed * 1000).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                          <div className="order-status">
                            {getStatusIcon(order.status)}
                            <span className={`status-text ${order.status}`}>
                              {getStatusText(order.status)}
                            </span>
                          </div>
                        </div>

                        <div className="account-order-items">
                          {order.items && order.items.slice(0, 3).map((item, index) => {
                            let options = null;
                            if (item.options) {
                              try {
                                options = typeof item.options === 'string' ? JSON.parse(item.options) : item.options;
                              } catch (e) {
                                console.error('Error parsing options:', e);
                              }
                            }
                            
                            const productUrl = createProductUrl(item.product_id, item.name);
                            
                            return (
                              <div key={index} className="account-order-item">
                                <div className="item-info">
                                  <span 
                                    className="item-name"
                                    onClick={() => navigate(productUrl)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    {item.name}
                                  </span>
                                  {options && options.modifiers && options.modifiers.length > 0 && (
                                    <div className="item-modifiers">
                                      {options.modifiers.map((modifier, idx) => (
                                        <span key={idx} className="modifier-tag">
                                          {modifier.name}: {modifier.option?.item || 'N/A'}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <span className="item-details">
                                  Qty: {item.qty} × {order.totals_curr} {parseFloat(item.amount).toFixed(2)}
                                </span>
                              </div>
                            );
                          })}
                          {order.items && order.items.length > 3 && (
                            <div className="account-order-item">
                              <span className="item-name">+{order.items.length - 3} more item(s)</span>
                            </div>
                          )}
                        </div>

                        <div className="order-footer">
                          <div className="order-total">
                            <strong>Total: {order.totals_curr} {parseFloat(order.totals).toFixed(2)}</strong>
                          </div>
                          <div className="order-actions">
                            <button 
                              className="view-order-btn"
                              onClick={() => navigate(`/account/order/${order.id}`)}
                            >
                              <RiEyeLine />
                              View details
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {ordersTotalPages > 1 && (
                    <div className="pagination">
                      <button 
                        className="pagination-btn"
                        onClick={() => setOrdersPage(prev => Math.max(1, prev - 1))}
                        disabled={ordersPage === 1}
                      >
                        Previous
                      </button>
                      <span className="pagination-info">
                        Page {ordersPage} of {ordersTotalPages}
                      </span>
                      <button 
                        className="pagination-btn"
                        onClick={() => setOrdersPage(prev => Math.min(ordersTotalPages, prev + 1))}
                        disabled={ordersPage === ordersTotalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="account-section">
              <div className="section-header">
                <h3>Account settings</h3>
                <p>Manage your account preferences</p>
              </div>

              <div className="settings-list">
                <div className="setting-item">
                  <div className="setting-info">
                    <h4>Password</h4>
                    <p>Update your password to keep your account secure</p>
                  </div>
                  <button className="setting-action" onClick={() => setActiveModal('password')}>Change password</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sign Out Confirmation Modal */}
      <Modal
        isOpen={activeModal === 'signout'}
        onClose={() => setActiveModal(null)}
        title="Sign out"
        size="small"
      >
        <div className="modal-confirmation">
          <div className="modal-icon">
            <RiLogoutBoxLine />
          </div>
          <div className="modal-message">
            Are you sure you want to sign out?
          </div>
          <div className="modal-submessage">
            You will need to sign in again to access your account.
          </div>
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setActiveModal(null)}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSignOut}>
              Sign out
            </button>
          </div>
        </div>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        isOpen={activeModal === 'password'}
        onClose={() => setActiveModal(null)}
        title="Change password"
        size="medium"
      >
        <div className="modal-form">
          <div className="modal-form-group">
            <label>Current password</label>
            <input
              type="password"
              name="currentPassword"
              value={passwordForm.currentPassword}
              onChange={handlePasswordChange}
              placeholder="Enter your current password"
            />
          </div>

          <div className="modal-form-group">
            <label>New password</label>
            <input
              type="password"
              name="newPassword"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
              placeholder="Enter your new password"
            />
          </div>

          <div className="modal-form-group">
            <label>Confirm new password</label>
            <input
              type="password"
              name="confirmPassword"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange}
              placeholder="Confirm your new password"
            />
          </div>

          <div className="modal-actions">
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                setActiveModal(null);
                setPasswordForm({
                  currentPassword: '',
                  newPassword: '',
                  confirmPassword: ''
                });
              }}
            >
              Cancel
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handlePasswordSubmit}
              disabled={isLoading || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
            >
              {isLoading ? 'Updating...' : 'Update password'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Account;